import type {PartialOptions} from "../../stem-core/ui/UIBase";
import {type UIChild, UI} from "../../stem-core/ui/UIBase";
import {styleRule, styleRuleInherit} from "../../stem-core/decorators/Style";
import {registerStyle} from "../../stem-core/ui/style/Theme";
import {NOOP_FUNCTION} from "../../stem-core/base/Utils.js";
import {normalize} from "../Utils.js";
import {KeyEvent} from "../KeyEvent.js";
import {BlinkInput, BlinkInputStyle} from "./Input.jsx";

// An entry of the autocompleteList option: what the input matches on, and what selecting it fills in
interface AutocompleteOption {
    name: string;
    display: string;
}


class InputAutocompleteStyle extends BlinkInputStyle {
    @styleRuleInherit
    container = {
        position: "relative",
    };

    @styleRule
    suggestionContainer = {
        position: "absolute",
        width: "100%",
        zIndex: "9999",
        background: this.themeProps.INPUT_BACKGROUND,
        border: () => "1px solid " + this.themeProps.INPUT_BORDER_COLOR, // TODO: To add in merchant input.
    };

    @styleRule
    suggestionItem = {
        height: 40,
        display: "flex",
        alignItems: "center",
        paddingLeft: 12,
        fontSize: this.themeProps.FONT_SIZE_NORMAL,
        cursor: "pointer",
        ":hover": {
            background: this.themeProps.INPUT_BACKGROUND_HOVER,
        },
    };
}

interface InputAutocompleteOptions {
    autocompleteList?: any;
    maxSuggestionCount?: number;
    minAutocompleteStringLength?: any;
    onEnter?: (...args: any[]) => void;
    onOptionSelect?: (...args: any[]) => void;
}

@registerStyle(InputAutocompleteStyle)
export class InputAutocomplete extends BlinkInput<InputAutocompleteOptions> {
    // The suggestion currently highlighted, or null when the typed value matches nothing
    declare selectedOption?: any;
    declare shouldShowAutocompleteOptions?: boolean;
    getDefaultOptions(): PartialOptions<InputAutocomplete> {
        return {
            ...super.getDefaultOptions(),
            autocompleteList: [],
            maxSuggestionCount: 3,
            minAutocompleteStringLength: 2,
            onOptionSelect: NOOP_FUNCTION,
        };
    }

    constructor(...args: ConstructorParameters<typeof BlinkInput>) {
        super(...args);
        this.shouldShowAutocompleteOptions = false;
        this.selectedOption = null;
    }

    getChildrenToRender() {
        return [
            ...(super.getChildrenToRender() as UIChild[]),
            this.renderAfterInput(),
        ]
    }

    isAutocompleteOption(option: string) {
        const formattedOption = normalize(option);
        const inputValue = normalize(this.getValue());

        if (inputValue.length < this.options.minAutocompleteStringLength) {
            return false;
        }
        if (formattedOption.length < inputValue.length) {
            return false;
        }
        for (let i = 0; i < inputValue.length; i += 1) {
            if (formattedOption[i].toLowerCase() !== inputValue[i].toLowerCase()) {
                return false;
            }
        }
        return true;
    }

    getAutocompleteItemsToShow() {
        return this.options.autocompleteList
            .filter((option: AutocompleteOption) => this.isAutocompleteOption(option.name))
            .slice(0, this.options.maxSuggestionCount);
    }

    selectOption(option: AutocompleteOption) {
        if (!option) {
            return;
        }
        this.setValue(option.display);
        this.selectedOption = option;
        this.dispatch("optionSelected", option);
    }

    // TODO @input this seems slow for some reason
    renderAfterInput() {
        const {styleSheet} = this;
        const autocompleteItems = this.getAutocompleteItemsToShow();

        return [
            this.shouldShowAutocompleteOptions && (autocompleteItems.length > 0) && (
                <div className={styleSheet.suggestionContainer}>
                    {autocompleteItems.map((option: AutocompleteOption) => (
                        <div className={styleSheet.suggestionItem} onClick={() => this.selectOption(option)}>
                            {option.name}
                        </div>
                    ))}
                </div>
            ),
        ];
    }

    setValue(value: string) {
        super.setValue(value);
        this.selectedOption = value.trim().length ? this.getExactlyMatchedOption() : null;
    }

    hideAutocompleteOptions() {
        this.shouldShowAutocompleteOptions = false;
        this.redraw();
    }

    showAutocompleteOptions() {
        this.shouldShowAutocompleteOptions = true;
        this.redraw();
    }

    getExactlyMatchedOption() {
        const inputValue = this.getValue().trim();
        return this.options.autocompleteList.find(({name}: AutocompleteOption) => {
            return inputValue.toLowerCase() === name.toLowerCase();
        });
    }

    initFocusListeners() {
        super.initFocusListeners();

        this.input.addNodeListener("blur", () => {
            if (!this.selectedOption) {
                const matchedOption = this.getExactlyMatchedOption() || this.selectedOption;
                this.selectOption(matchedOption);
            }
            this.options.onBlur();
        });

        this.input.addNodeListener("focus", (event) => {
            if (!this.selectedOption || this.selectedOption.name !== this.getValue().trim()) {
                this.showAutocompleteOptions();
            }
            this.options.onFocus(event);
        });

        document.body.addEventListener("click", event => {
            if (this.input && event.target !== this.input.node) {
                this.hideAutocompleteOptions();
            }
        });

        this.addListener("optionSelected", (option: AutocompleteOption) => {
            this.options.onOptionSelect(option);
        });
    }

    initKeyListeners() {
        super.initKeyListeners();

        this.input.addNodeListener("keydown", event => {
            const key = new KeyEvent(event);
            if (key.isTab()) {
                this.hideAutocompleteOptions();
            }
        });

        this.input.addNodeListener("keyup", event => {
            this.clearError();
            this.selectedOption = null;
            this.showAutocompleteOptions();
            const key = new KeyEvent(event);

            if (key.isEnter()) {
                this.options.onEnter();
            }

            if (key.isEscape()) {
                this.hideAutocompleteOptions();
            }
        });
    }

    onMount() {
        super.onMount();
        this.showAutocompleteOptions();
    }
}
